import {
  BuriedCore,
  type BuriedCoreConfig,
  type BuriedData,
  type BuriedDirectiveValue,
  type TrackingAdapter,
} from '@rendora/common/services/buried/core';
import { createAliTrackingAdapter } from '@rendora/common/services/buried/integrations/ali';
import type { DirectiveBinding, ObjectDirective } from 'vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';

/*
 * 埋点使用示例：
 *
 * 1. 自动埋点（基于data-buried属性）：
 *    <button data-buried="BUTTON_CLICK" data-buried-params='{"button_type": "primary", "page": "home"}'>
 *      点击按钮
 *    </button>
 *
 * 2. 使用埋点指令：
 *    <button v-buried="{ eventName: 'CUSTOM_CLICK', customData: { action: 'submit' } }"
 *            data-buried-params='{"form_id": "login_form"}'>
 *      提交表单
 *    </button>
 *
 * 3. 手动埋点：
 *    const { track } = useBuriedTracking();
 *    track('MANUAL_EVENT', { user_id: 123, action: 'purchase' });
 *
 * 4. 动态元素埋点：
 *    支持 SPA 中动态渲染的元素，自动监听新添加的埋点元素
 *    如需手动重新观察：const { reobserve } = useBuriedTracking();
 *    reobserve();
 *
 * data-buried-params 支持两种格式：
 * - JSON格式：data-buried-params='{"key": "value", "number": 123}'
 * - URL参数格式：data-buried-params="key=value&number=123"
 */

export type BuriedConfig = BuriedCoreConfig;

const DEFAULT_CONFIG: Required<Omit<BuriedConfig, 'customEvents'>> & {
  customEvents: Record<string, unknown>;
} = {
  autoTrack: true,
  enableClick: true,
  enableExposure: true,
  exposureThreshold: 0.5,
  customEvents: {},
  enableDevLog: false,
  enableWebVitals: true,
  webVitalsConfig: {
    reportAllChanges: false,
    durationThreshold: 40,
  },
  clickBlacklist: {
    tagNames: ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon'],
    classNames: [],
    selectors: [],
  },
};

let buriedInstance: BuriedTracker | null = null;

export class BuriedTracker {
  private config: BuriedConfig;
  private core: BuriedCore;
  private directive: ObjectDirective<HTMLElement, BuriedDirectiveValue>;
  private readonly trackingAdapters: TrackingAdapter[];

  constructor(config: BuriedConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.trackingAdapters = [createAliTrackingAdapter()];
    this.core = new BuriedCore({
      config: this.config,
      send: data => this.sendBuriedData(data),
      trackingAdapters: this.trackingAdapters,
    });
    this.directive = this.core.createDirective();
  }

  // 初始化埋点
  public init(): void {
    this.core.init();
  }

  // 销毁埋点
  public destroy(): void {
    this.core.destroy();
  }

  // 手动埋点
  public track(eventName: string, customData?: Record<string, unknown>): void {
    this.core.track(eventName, customData);
  }

  // 用户登录
  public login(userId: number | string): void {
    this.core.init();
    this.core.login(userId);
  }

  // 重新观察所有埋点元素
  public reobserveElements(): void {
    this.core.reobserveElements();
  }

  // 指令挂载处理
  public mountDirective(el: HTMLElement, binding: DirectiveBinding<BuriedDirectiveValue>): void {
    const mountedHandler = this.directive.mounted as
      | ((element: HTMLElement, directiveBinding: DirectiveBinding<BuriedDirectiveValue>) => void)
      | undefined;
    mountedHandler?.(el, binding);
  }

  // 发送埋点数据
  private sendBuriedData(data: BuriedData): void {
    // SSR 环境下不执行
    if (import.meta.server || typeof window === 'undefined') return;

    const trackName = `global_buried_${data.event_type}`;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Buried Event:', data);
      return;
    }

    if (window._yfx_trackdata) {
      window._yfx_trackdata.push(['event', 'buried', trackName, data]);
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: trackName,
        ...data,
      });
    }
  }
}

// 创建埋点实例
export function createBuriedTracker(config?: BuriedConfig): BuriedTracker {
  if (!buriedInstance) {
    buriedInstance = new BuriedTracker(config);
  }
  return buriedInstance;
}

// 获取埋点实例
export function getBuriedTracker(): BuriedTracker | null {
  return buriedInstance;
}

// Vue组合式函数：自动埋点
export function useBuriedTracking(config?: BuriedConfig) {
  const tracker = ref<BuriedTracker | null>(null);

  onMounted(() => {
    tracker.value = createBuriedTracker(config);
    tracker.value.init();
  });

  onBeforeUnmount(() => {
    tracker.value?.destroy();
    tracker.value = null;
  });

  return {
    tracker,
    track: (eventName: string, customData?: Record<string, unknown>) => {
      tracker.value?.track(eventName, customData);
    },
    reobserve: () => {
      tracker.value?.reobserveElements();
    },
  };
}

// 全局埋点指令
export const buriedDirective: ObjectDirective<HTMLElement, BuriedDirectiveValue> = {
  mounted(el, binding) {
    if (import.meta.server) return;

    const tracker = getBuriedTracker();
    if (!tracker) {
      return;
    }

    tracker.mountDirective(el, binding);
  },
};

// 自动埋点插件
export default defineNuxtPlugin(() => {
  if (import.meta.server) return;

  const tracker = createBuriedTracker();
  tracker.init();

  // 在应用卸载时清理埋点实例
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      tracker.destroy();
      buriedInstance = null;
    });
  }

  return {
    provide: {
      buriedTracker: tracker,
    },
  };
});
